import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowLeftIcon } from '@pkg/assets/icons/v2/arrow-left.svg';
import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import {
    AuthBackUpCodes,
    AuthChooseType,
    AuthConfirmation,
    AuthFlowFooter,
    AuthFlowMobileHeader,
    AuthSetupAuthenticator,
    AuthSetupEnterCode,
    AuthSetupPhoneNumber,
    AuthSideNav,
    LogoLink,
    YatMagnetism,
} from '@pkg/components';
import YatFooter from '@pkg/components/Footer/Footer';
import { useAppDispatch, useMobile } from '@pkg/hooks';

import { ArtifactMintSteps, NftMintSteps } from '@app/client';
import { setArtifactMintPanelOpen, setArtifactMintPanelStep } from '@app/store/actions/artifacts';
import { setNftMintPanelStep, setNftPanelOpen } from '@app/store/actions/nft';
import { hide2faSetupFlow } from '@app/store/slices';

import { AuthenticatorStep, AuthenticatorType } from '../types';

import './AuthAppFlow.scss';

export const AuthAppFlow = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(['twoFactorAuth']);
    const { isMobile } = useMobile();
    const [authStep, setAuth] = useState<AuthenticatorStep>(AuthenticatorStep.ChooseMethod);
    const [authType, setAuthType] = useState<AuthenticatorType>(AuthenticatorType.PhoneNumber);
    const [phone, setPhone] = useState('');
    const mintFlow = !!parseInt(localStorage.getItem('mintingAttempted') || '0');
    const artifactMintFlow = !!parseInt(localStorage.getItem('artifactMintingAttempted') || '0');

    const onCancelFlow = () => {
        if (mintFlow) {
            localStorage.removeItem('mintingAttempted');
        }
        if (artifactMintFlow) {
            localStorage.removeItem('artifactMintingAttempted');
        }
        dispatch(hide2faSetupFlow());
    };

    const onStepBack = useCallback(() => {
        if (authStep === AuthenticatorStep.ChooseMethod) {
            if (mintFlow) {
                localStorage.removeItem('mintingAttempted');
            }
            if (artifactMintFlow) {
                localStorage.removeItem('artifactMintingAttempted');
            }
            dispatch(hide2faSetupFlow());
        }
        if (authStep === AuthenticatorStep.BackupCodes) {
            setAuth(AuthenticatorStep.Confirmation);
        } else if (authStep === AuthenticatorStep.Confirmation) {
            setAuth(AuthenticatorStep.SetupAuthenticatorEnterCode);
        } else if (authStep === AuthenticatorStep.SetupAuthenticatorEnterCode) {
            setAuth(AuthenticatorStep.SetupAuthenticator);
        } else if (authStep === AuthenticatorStep.SetupAuthenticator) {
            setAuth(AuthenticatorStep.ChooseMethod);
        }
    }, [authStep, dispatch, mintFlow, artifactMintFlow]);

    const mobileTitle = useMemo(() => {
        if (authStep === AuthenticatorStep.ChooseMethod) {
            return t('2fa_mobile_nav_setup_2fa');
        }

        if (authStep === AuthenticatorStep.SetupAuthenticator) {
            if (authType === AuthenticatorType.AuthenticatorApp) {
                return t('2fa_mobile_nav_ga_config');
            } else {
                return t('2fa_mobile_nav_sms_config');
            }
        }

        if (authStep === AuthenticatorStep.SetupAuthenticatorEnterCode) {
            return t('2fa_mobile_nav_verification');
        }

        if (authStep === AuthenticatorStep.Confirmation) {
            return t('2fa_mobile_nav_confirmation');
        }

        return '';
    }, [authStep, authType, t]);

    const authStepContent = useMemo(() => {
        const handleConfirm = () => {
            dispatch(hide2faSetupFlow());

            if (mintFlow) {
                dispatch(setNftPanelOpen(true));
                dispatch(setNftMintPanelStep(NftMintSteps.Introduction));
                localStorage.removeItem('mintingAttempted');
            }
            if (artifactMintFlow) {
                dispatch(setArtifactMintPanelOpen(true));
                dispatch(setArtifactMintPanelStep(ArtifactMintSteps.Select));
                localStorage.removeItem('artifactMintingAttempted');
            }
        };

        if (authStep === AuthenticatorStep.ChooseMethod) {
            return (
                <AuthChooseType
                    onChooseType={(type) => {
                        setAuthType(type);
                        setAuth(AuthenticatorStep.SetupAuthenticator);
                    }}
                />
            );
        }

        if (authStep === AuthenticatorStep.SetupAuthenticator) {
            if (authType === AuthenticatorType.AuthenticatorApp) {
                return <AuthSetupAuthenticator onNext={() => setAuth(AuthenticatorStep.SetupAuthenticatorEnterCode)} />;
            } else {
                return (
                    <AuthSetupPhoneNumber
                        onNext={(phoneNumber: string) => {
                            setAuth(AuthenticatorStep.SetupAuthenticatorEnterCode);
                            setPhone(phoneNumber);
                        }}
                        onSwitchType={() => {
                            setAuth(AuthenticatorStep.SetupAuthenticator);
                            setAuthType(AuthenticatorType.AuthenticatorApp);
                        }}
                    />
                );
            }
        }

        if (authStep === AuthenticatorStep.SetupAuthenticatorEnterCode) {
            return (
                <AuthSetupEnterCode
                    onNext={() => setAuth(AuthenticatorStep.Confirmation)}
                    authType={authType}
                    phone={phone}
                />
            );
        }

        if (authStep === AuthenticatorStep.Confirmation) {
            return <AuthConfirmation type={authType} onConfirm={handleConfirm} />;
        }

        if (authStep === AuthenticatorStep.BackupCodes) {
            return <AuthBackUpCodes />;
        }

        return null;
    }, [dispatch, authStep, authType, phone, mintFlow, artifactMintFlow]);

    const authStepFooter = useMemo(() => {
        if (authStep === AuthenticatorStep.ChooseMethod) {
            return (
                <div className="two-factor-app-flow__footer">
                    <AuthFlowFooter />
                </div>
            );
        }
        if (
            !isMobile &&
            authType === AuthenticatorType.AuthenticatorApp &&
            authStep === AuthenticatorStep.SetupAuthenticator
        ) {
            return (
                <div className="two-factor-app-flow__footer">
                    <AuthFlowFooter />
                </div>
            );
        }
        return null;
    }, [authType, authStep, isMobile]);

    const hasCloseButton = useMemo(() => {
        if (authStep === AuthenticatorStep.Confirmation) {
            return false;
        }
        return isMobile;
    }, [authStep, isMobile]);

    const hasBackButton = useMemo(() => {
        if (authStep === AuthenticatorStep.Confirmation) {
            return false;
        }
        return !(isMobile && [AuthenticatorStep.ChooseMethod, AuthenticatorStep.Confirmation].includes(authStep));
    }, [authStep, isMobile]);

    return (
        <div className="two-factor-app-flow">
            {authStep === AuthenticatorStep.Confirmation && <LogoLink symbolOnly={true} classes="yat-2fa-logo" />}
            {hasCloseButton && (
                <YatMagnetism classes="yat-2fa-prev-button">
                    <div className={`yat-circle-button`} onClick={onStepBack}>
                        <ArrowLeftIcon />
                    </div>
                </YatMagnetism>
            )}
            <AuthFlowMobileHeader title={mobileTitle} />
            {hasBackButton && (
                <YatMagnetism classes="yat-2fa-back-button">
                    <div
                        className={`yat-circle-button ${!isMobile && 'yat-circle-button--large'}`}
                        onClick={onCancelFlow}
                    >
                        <CloseIcon />
                    </div>
                </YatMagnetism>
            )}
            <AuthSideNav step={authStep} type={authType} onClick={(step) => setAuth(step)} />
            <div className="two-factor-app-flow__container">
                <div className="two-factor-app-flow__container-content">{authStepContent}</div>
                {authStepFooter}
            </div>
            {!isMobile && <YatFooter wordsCareers={false} showCareers={false} />}
        </div>
    );
};
